import { apiRequest } from './api';
import { API_BASE_URL } from '../config/api';

export interface PicklistItem {
  id: string;
  clientName: string;
  clientAddress?: string;
  propertyName?: string;
  propertyAddress?: string;
  primaryContact?: string;
  contactEmail?: string;
  contactPhone?: string;
  utilityType: string;
  accountNumber: string;
  providerName: string;
  billingFrequency?: string;
  lastInvoiceDate?: string;
  nextExpectedDate: string;
  status: 'pending' | 'overdue' | 'missing' | 'collected';
  notes?: string;
  /**
   * True when the picklist item tracks a supplier contract (no property tie).
   * UI uses this to render a "Supplier" badge/highlight so users see at a glance
   * that the row covers a whole supplier contract, not a single property.
   */
  isSupplier?: boolean;
}

export interface PicklistStats {
  dueThisWeek: number;
  overdue: number;
  missing: number;
  collected: number;
}

// Build a query string from optional filters. Returns '' (no leading '?') when no
// filters are present so the URL stays clean.
function buildPicklistQuery(filters: { status?: string; clientName?: string }): string {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.clientName) qs.set('client_name', filters.clientName);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const picklistApi = {
  async getItems(status?: string, clientName?: string): Promise<PicklistItem[]> {
    const params = buildPicklistQuery({ status, clientName });
    return apiRequest<PicklistItem[]>(`/api/picklist/items${params}`);
  },

  async getStats(): Promise<PicklistStats> {
    return apiRequest<PicklistStats>('/api/picklist/stats');
  },

  async getItem(id: string): Promise<PicklistItem> {
    return apiRequest<PicklistItem>(`/api/picklist/items/${id}`);
  },

  async updateItem(id: string, data: { status: string; notes?: string }) {
    return apiRequest(`/api/picklist/items/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  },

  async markAsCollected(id: string, uploadedFileId: string) {
    return apiRequest(`/api/picklist/items/${id}/collect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uploaded_file_id: uploadedFileId }),
    });
  },

  async exportCsv(status?: string, clientName?: string): Promise<void> {
    // The export endpoint requires the same JWT as every other API call.
    // Using window.location.href would send a plain browser navigation with no
    // Authorization header and get rejected by the auth middleware (401
    // "No token provided"). Fetch with the bearer token, then trigger the
    // download client-side via a Blob + synthetic <a download>.
    const params = buildPicklistQuery({ status, clientName });
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Not authenticated — please sign in again before exporting.');
    }

    const response = await fetch(`${API_BASE_URL}/api/picklist/export/csv${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      // Try to surface the server's error message; fall back to status text.
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (body?.error) detail = body.error;
      } catch { /* response wasn't JSON — keep statusText */ }
      throw new Error(`Picklist CSV export failed (HTTP ${response.status}): ${detail}`);
    }

    // Honor the backend's Content-Disposition filename when present;
    // otherwise generate a sensible default.
    const cd = response.headers.get('Content-Disposition') || '';
    const filenameMatch = cd.match(/filename="?([^";]+)"?/);
    const filename = filenameMatch?.[1] || `picklist-${new Date().toISOString().split('T')[0]}.csv`;

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Release the object URL so the browser can free the memory.
    URL.revokeObjectURL(objectUrl);
  }
};